import { AxisIndexItem, AxisResult, DeepPartial, IndicatorResult } from "../../types";
import { ReportResult } from "../../model/reportResult";
import { ChartVisualization } from "./chart";
import { ContextData, ChartViewOptions, ReportContext } from "@reporting/rp-types/analytics";
import { dom, html, numbers, utils } from "@omegagrid/core";
import { getLocale } from "@omegagrid/localize";
import { DefaultChartAdapter } from "./chartAdapter";
import { sortChartData } from "./chartSorting";
import type { ChartPointDrill } from "./chartController";
import type { SeriesOptionsType } from "highcharts";

/** custom data of a chart point: the drill down info and the number format of its value */
export type ChartPointCustom = ChartPointDrill & {
	/** number format of the indicator (excel style, e.g. #,0.0%), formats the tooltip and the labels */
	format?: string;
};

/** custom data of a series: the number format shared by its points (when the series is one indicator) */
export type ChartSeriesCustom = {
	format?: string;
};

/** value formatted by the excel style format of the indicator (percentage, decimals, unit) */
export const formatChartValue = (value: number, format?: string): string => {
	if (value == null || !isFinite(value)) return '';
	return numbers.getNumberFormatter(getLocale(), format || '')(value);
};

const chartTypeOptionMap = new Map<string, DeepPartial<Highcharts.Options>>([
	['column', {chart: {type: 'column'}, plotOptions: {column: {stacking: null}}}],
	['column-stacked', {chart: {type: 'column'}, plotOptions: {column: {stacking: 'normal'}}}],
	['bar', {chart: {type: 'bar'}, plotOptions: {bar: {stacking: null}}}],
	['bar-stacked', {chart: {type: 'bar'}, plotOptions: {bar: {stacking: 'normal'}}}],
	['line', {chart: {type: 'line'}}],
	['spline', {chart: {type: 'spline'}}],
	['area', {chart: {type: 'area'}}],
	['pie', {chart: {type: 'pie'}}],
	['scatter', {chart: {type: 'scatter'}}],
	// ['waterfall', {chart: {type: 'waterfall'}}],
]);

export class ChartFactory {

	private contextData: ContextData;

	constructor(
		private result: ReportResult,
		private reportContext: ReportContext
	) {
		this.contextData = reportContext.context as ContextData;
	}

	/** swapped meaning of the input rows (y) and columns (x) */
	get inverted(): boolean {
		let inverted = !!(this.contextData.view_options as ChartViewOptions)?.inverted;
		if (this.contextData.view == 'pie' || this.contextData.view == 'donut') inverted = !inverted;
		return inverted;
	}

	/** split into a chart per item of the second axis level, on by default */
	get split(): boolean {
		return (this.contextData.view_options as ChartViewOptions)?.split !== false;
	}

	/** axis holding the series - rows (y) by default, columns (x) when inverted */
	private get seriesSourceAxes(): AxisResult[] {
		return this.inverted ? this.result.xAxis : this.result.yAxis;
	}

	/** axis holding the categories - columns (x) by default, rows (y) when inverted */
	private get categorySourceAxes(): AxisResult[] {
		return this.inverted ? this.result.yAxis : this.result.xAxis;
	}

	private _chartAxis: AxisResult;
	get chartAxis(): AxisResult {
		if (!this.split) return null;
		if (this._chartAxis) return this._chartAxis;
		if (this.seriesSourceAxes.length > 1) this._chartAxis = this.seriesSourceAxes[1];
		if (this.categorySourceAxes.length > 1) this._chartAxis = this.categorySourceAxes[1];
		return this._chartAxis;
	}

	private _seriesAxis: Partial<AxisResult>;
	get seriesAxis() {
		if (this._seriesAxis) return this._seriesAxis;

		// Series are on the first level of the series source axis (Y by default)
		if (this.seriesSourceAxes?.length) {
			this._seriesAxis = this.seriesSourceAxes[0];
		} else if (this.categorySourceAxes?.length > 1) {
			// If no series axis, but we have multiple category axes, series can be on second to last
			this._seriesAxis = this.categorySourceAxes[this.categorySourceAxes.length - 2];
		} else {
			// No axes - empty series
			this._seriesAxis = {
				axisLevel: 0,
				axisIndex: 1,
				axisType: 'empty'
			};
		}

		return this._seriesAxis;
	}

	private _series: AxisIndexItem[][];
	get seriesItems() {
		if (this._series) return this._series;
		this._series = [];

		// Get the correct index based on which axis categoryAxis is on
		const index = this.seriesAxis.axisIndex == 0 ? this.result.xIndex : this.result.yIndex;
		const seriesLevel = this.seriesAxis.axisLevel;
		const chartAxisLevel = this.chartAxis?.axisIndex == this.seriesAxis.axisIndex ? this.chartAxis.axisLevel : -1;

		this.chartItems.forEach(chartItem => {
			const chartItemIndex = chartAxisLevel >= 0 ? chartItem[1] : null;
			const series = index.filter(item =>
				item[2] == seriesLevel && (chartItemIndex === null || item[1][chartAxisLevel] == chartItemIndex)
			);
			this._series.push(series);
		});

		return this._series;
	}

	private _categoryAxis: AxisResult;
	get categoryAxis() {
		if (this._categoryAxis) return this._categoryAxis;
		// first level of the category source axis (X by default), the other
		// source is a fallback for a one axis result
		this._categoryAxis = this.categorySourceAxes[0] ?? this.seriesSourceAxes[0];
		return this._categoryAxis;
	}

	private _chartItems: [number, number][];
	get chartItems(): [number, number][] {
		if (this._chartItems) return this._chartItems;
		this._chartItems = [];

		if (this.chartAxis?.axisType == 'indicators') {
			this.result.indicators.forEach((ind, i) => {
				this._chartItems.push([null, null]);
			});
		} else if (this.chartAxis?.index) {
			this.chartAxis.index.forEach((value, key) => {
				this._chartItems.push([key, value]);
			});
		} else {
			this._chartItems.push([null, null]);
		}

		return this._chartItems;
	}

	private _categories: AxisIndexItem[][];
	get categories() {
		if (this._categories) return this._categories;
		this._categories = [];

		// Get the correct index based on which axis categoryAxis is on
		const index = this.categoryAxis.axisIndex == 0 ? this.result.xIndex : this.result.yIndex;
		const categoryLevel = this.categoryAxis.axisLevel;
		const chartAxisLevel = this.chartAxis?.axisIndex == this.categoryAxis.axisIndex ? this.chartAxis.axisLevel : -1;

		if (this.categoryAxis.axisType == 'indicators') {
			// no chart axis (single chart): every indicator item is a category
			this.chartItems.forEach((chartItem) => {
				this._categories.push(index.filter(i => chartItem[1] == null || i[1][i[2]] == chartItem[1]));
			});
		} else if (this.seriesAxis.axisType == 'indicators') {
			this.chartItems.forEach((chartItem, chartIndex) => {
				this._categories.push(index.filter(i => {
					return (i[1][i[2]] == chartItem[1] || chartItem[1] == null) && i[1].length - 1 == i[2]; // take last level only
				}));
			});
		} else if (!this.split) {
			// no chart splitting: all the leaf combinations of the category
			// axes make the categories of the single chart (the suppressed
			// chart axis may be null, this has to precede its branch)
			this._categories.push(index.filter(i => i[1].length - 1 == i[2]));
		} else if (this.chartAxis.axisType == 'indicators') {
			this.chartItems.forEach((_, chartIndex) => {
				this._categories.push(index.filter(i => i[1][i[2]] == chartIndex));
			});
		} else {
			this.chartItems.forEach((chartItem) => {
				const chartItemIndex = chartAxisLevel >= 0 ? chartItem[1] : null;
				this._categories.push(index.filter(i => i[2] == categoryLevel && (
					chartItemIndex === null || i[1][chartAxisLevel] == chartItemIndex
				)));
			});
		}

		return this._categories;
	}

	private getCategoryNames(chartIndex: number) {
		if (this.categoryAxis.axisType == 'indicators') {
			return this.result.indicators.map(indicator => indicator.label);
		} else {
			return (this.categories[chartIndex] || []).map(
				item => {
					return this.categoryAxis.data[item[1][this.categoryAxis.axisLevel]][this.categoryAxis.columnIndex.dim];
				}
			);
		}
	}

	private getDefaultOptions() {
		return chartTypeOptionMap.get(this.contextData.view) ?? Array.from(chartTypeOptionMap.entries())[0][1];
	}

	private getChartType(): string {
		return this.getDefaultOptions().chart?.type;
	}

	private getYTitle(chartIndex: number): string {
		const chartAxis = this.chartAxis;
		if (!chartAxis) return undefined;
		const chartItem = this.chartItems[chartIndex];

		if (chartAxis.axisType == 'indicators') {
			const indicator = this.result.indicators[chartIndex];
			return html.escapeHTML(indicator?.label || indicator?.label || indicator?.dataColumn);
		} else {
			const dataIndex = Array.isArray(chartItem[1]) ? chartItem[1][chartAxis.axisLevel ?? 0] : chartItem[1];
			const text = chartAxis.data[dataIndex]?.[chartAxis.columnIndex.dim] ?? '';
			return html.escapeHTML(text);
		}
	}

	/**
	 * Indicator carried by a category item. When the indicator axis lies on
	 * the category side, the indicator index sits at its axis level of the
	 * item indexes (e.g. indicators above the periods: I at level 0, PM at
	 * level 1) - the deepest level of the item may point elsewhere.
	 */
	private getCategoryIndicator(categoryItem: AxisIndexItem): IndicatorResult {
		const indicatorAxis = this.result.indicatorAxis;
		const level = indicatorAxis && indicatorAxis.axisIndex == this.categoryAxis.axisIndex
			? indicatorAxis.axisLevel
			: categoryItem?.[2];
		return this.result.indicators[categoryItem?.[1]?.[level] ?? 0];
	}

	/**
	 * Value of a data point - null for a missing cell (a gap in the chart),
	 * zero stays a point. The display unit of the indicator divides the value
	 * the same way the table does (percentages are never scaled).
	 */
	private getDataPoint(
		categoryItem: AxisIndexItem,
		serieItem: AxisIndexItem,
		indicator?: IndicatorResult
	): {y: number|null} {
		indicator ??= this.getCategoryIndicator(categoryItem);
		// dataIndex rows are keyed by the y items and indexed by the x items;
		// series sit on y by default, on x when inverted
		const [yItem, xItem] = this.inverted ? [categoryItem, serieItem] : [serieItem, categoryItem];
		const row = this.result.dataIndex.get(yItem?.[0]);
		if (!row || !indicator) return {y: null};
		const index = row[xItem ? xItem[0] : 0];
		if (index == null) return {y: null};
		const raw = this.result.data.data[index][indicator.dataColumnIndex];
		if (raw == null || raw === '') return {y: null};
		let value = parseFloat(raw);
		if (!isFinite(value)) return {y: null};
		if (indicator.displayUnit > 1 && !indicator.format?.endsWith('%')) value /= indicator.displayUnit;
		return {y: value};
	}

	private getSerie(chartIndex: number, serieIndex: number, indicator?: IndicatorResult): SeriesOptionsType {
		const serieItem = this.seriesItems[chartIndex]?.[serieIndex];
		const type = this.getChartType() as any;

		if (!serieItem) {
			return {
				name: '',
				type: type,
				data: this.getCategoryNames(chartIndex).map(name => ({name: html.escapeHTML(name ?? ''), y: 0})),
			};
		}

		// with the indicators on the category axis (e.g. inverted) the serie
		// item indexes a dimension, the indicator is resolved per data point
		indicator ??= this.categoryAxis.axisType == 'indicators' && this.seriesAxis.axisType != 'indicators'
			? null
			: this.result.indicators[serieItem?.[1]?.[serieItem[2]] ?? 0];

		const name = html.escapeHTML((this.seriesAxis.axisType == 'indicators'
			? indicator.label
			: this.seriesAxis.data[serieItem[1][serieItem[2]]][this.seriesAxis.columnIndex.dim]
		) ?? '');

		// Get categories and compute data for this chart
		const categories = this.categories[chartIndex] || [];
		const categoryNames = this.getCategoryNames(chartIndex);
		// Pie charts ignore xAxis.categories and label slices by point name,
		// falling back to lang.pieSliceName ("Slice") when missing
		const data = categories.map(
			(categoryItem, i) => {
				const point: {y: number|null, name?: string, custom?: ChartPointCustom} = this.getDataPoint(categoryItem, serieItem, indicator);
				if (point.y == null) return null;
				const pointIndicator = indicator ?? this.getCategoryIndicator(categoryItem);
				point.name = html.escapeHTML(categoryNames[i] ?? '');
				point.custom = {
					...this.getPointDrill(categoryItem, serieItem, pointIndicator),
					format: pointIndicator?.format,
				};
				return point;
			}
		);

		// one indicator per series: its format is shared by the points (the
		// axis labels use it too when every series of the chart agrees)
		const custom: ChartSeriesCustom = {format: indicator?.format};

		return { type, name, data, custom } as SeriesOptionsType;
	}

	/**
	 * Number format shared by every series of a chart, undefined when the
	 * series mix formats (e.g. an amount next to a percentage).
	 */
	private getChartFormat(series: SeriesOptionsType[]): string|undefined {
		const formats = new Set(series.map(serie => (serie as any).custom?.format ?? ''));
		return formats.size === 1 ? [...formats][0] || undefined : undefined;
	}

	/**
	 * Tooltip, data label and value axis label formatters applying the number
	 * format of the indicator (percentages, decimals, units) the same way the
	 * table does. The point format wins over the series format - with the
	 * indicators on the category axis every point carries its own.
	 */
	private getFormatOptions(series: SeriesOptionsType[]): DeepPartial<Highcharts.Options> {
		const chartFormat = this.getChartFormat(series);
		const pointFormat = (point: any) => point?.options?.custom?.format ?? point?.custom?.format ?? point?.series?.options?.custom?.format;

		return {
			tooltip: {
				pointFormatter: function(this: any) {
					const value = formatChartValue(this.y, pointFormat(this));
					return `<span style="color:${this.color}">●</span> ${this.series.name}: <b>${value}</b><br/>`;
				},
			},
			plotOptions: {
				series: {
					dataLabels: {
						formatter: function(this: any) {
							const value = formatChartValue(this.y, pointFormat(this.point));
							// pie slices are labeled by name (the highcharts default), the value follows
							return this.series?.type === 'pie' ? `${this.point.name}: ${value}` : value;
						},
					},
				},
			},
			...(chartFormat ? {
				yAxis: {
					labels: {
						formatter: function(this: any) {
							return formatChartValue(this.value, chartFormat);
						},
					},
				},
			} : {}),
		};
	}

	/**
	 * Dimension items carried by an axis index item - one per dimension axis
	 * level of the axis side (the path holds an index for every level, so
	 * a chart split by the second level filters by its item too).
	 */
	private getItemDims(axes: AxisResult[], item: AxisIndexItem): {dimNum: string, id: string, dim: string}[] {
		const dims: {dimNum: string, id: string, dim: string}[] = [];
		(axes ?? []).forEach((axis, level) => {
			if (axis.axisType !== 'dimension') return;
			const dataRow = axis.data[item?.[1]?.[level]];
			if (!dataRow) return;
			dims.push({
				dimNum: axis.dimension,
				id: dataRow[axis.columnIndex.id],
				dim: dataRow[axis.columnIndex.dim],
			});
		});
		return dims;
	}

	/**
	 * Drill down info of a data point (see ChartController) - the dimension
	 * filters of both axis items and the indicator of the point value.
	 */
	private getPointDrill(categoryItem: AxisIndexItem, serieItem: AxisIndexItem, indicator: IndicatorResult): ChartPointDrill {
		// the axes the item path indexes into - the side its axis sits on
		const itemAxes = (axisIndex: number) => axisIndex == 0 ? this.result.xAxis : this.result.yAxis;
		const dims = [
			...this.getItemDims(itemAxes(this.categoryAxis.axisIndex), categoryItem),
			...this.getItemDims(itemAxes(this.seriesAxis.axisIndex), serieItem),
		];

		const filters: Record<string, string> = {};
		dims.forEach(item => filters[item.dimNum] = item.id);

		return {
			filters,
			indicator: indicator?.index,
			description: dims.map(item => item.dim).join(' | '),
		};
	}

	private getSeries(chartIndex: number) {
		const chartAxis = this.chartAxis;
		return (this.seriesItems[chartIndex] || []).map((item, j) => this.getSerie(chartIndex, j,
			chartAxis?.axisType == 'indicators' ? this.result.indicators[chartIndex] : null
		));
	}

	getChartOptions(): DeepPartial<Highcharts.Options>[] {
		const options = this.getDefaultOptions();
		const sorting = (this.contextData.view_options as ChartViewOptions)?.sorting;

		const result: DeepPartial<Highcharts.Options>[] = [];

		this.chartItems.forEach((chartItem, chartIndex) => {
			const {categories, series} = sortChartData(
				this.getCategoryNames(chartIndex).map(name => html.escapeHTML(name)),
				this.getSeries(chartIndex),
				sorting
			);

			result.push(utils.mergeDeep<DeepPartial<Highcharts.Options>>(options, this.getFormatOptions(series as SeriesOptionsType[]), {
				title: {
					text: '' //chartAxis ? chartAxis.data[chartItem[1]][chartAxis.dimColumnIndex] : undefined
				},
				xAxis: {
					categories,
				},
				yAxis: {
					title: {
						text: this.getYTitle(chartIndex),
						enabled: false
					} as any
				},
				legend: {
					layout: 'horizontal',
					align: 'center',
					verticalAlign: 'bottom',
					enabled: (this.seriesItems[chartIndex]?.length ?? 0) > 1
				},
				chart: {
					animation: false
				},
				plotOptions: {
					series: {
						animation: false
					}
				},
				series
			}));
		});

		return result;
	}

	/**
	 * Every adapter recomputes its chart options through a fresh factory, so
	 * a refresh picks up the view options affecting the generated data (e.g.
	 * inverted). The chart count stays as created - a change of the chart
	 * axis items only applies on the next full load.
	 */
	createChartAdapters(): DefaultChartAdapter[] {
		const {result, reportContext, contextData} = this;
		return this.getChartOptions().map((_, chartIndex) => new DefaultChartAdapter(
			() => (new ChartFactory(result, reportContext).getChartOptions()[chartIndex] ?? {}) as Highcharts.Options,
			contextData,
			reportContext
		));
	}

	createCharts(): ChartVisualization[] {
		return this.createChartAdapters().map(adapter => dom.createElement<ChartVisualization>('rp-visual-chart', {
			adapter: adapter,
			caption: (adapter.chartOptions.yAxis as any).title.text as string
		}));
	}

}
